import { computed } from "vue";
import store from "@/store/index";
import { GET_USER_DATA } from "@/store/getters/user";
import { routes } from "@/router/routes";
import {
  hasPermission,
  getDisplayRoleName,
  EPORTAL_BUILDING_APPLICATION_VIEW,
  EPORTAL_BUILDING_APPLICATION_CREATE,
  EPORTAL_ENFORCEMENT_VIEW,
  EPORTAL_SCHEME_PLAN_APPLICATION_CREATE,
  EPORTAL_SCHEME_PLAN_APPLICATION_VIEW,
} from "@/utils/permissions";

const ROLE_ICONS = {
  "County Architect": "mdi-drawing",
  "Structural Engineer": "mdi-bridge",
  "Physical/Town Planner": "mdi-city-variant-outline",
};

export const ACTION_CATEGORY = {
  BUILDING_PLANS: "building_plans",
  SCHEME_PLANS: "scheme_plans",
};

const CATEGORY_LABELS = {
  [ACTION_CATEGORY.BUILDING_PLANS]: "Building Plans",
  [ACTION_CATEGORY.SCHEME_PLANS]: "Scheme Plans",
};

const CATEGORY_SECTIONS = {
  [ACTION_CATEGORY.BUILDING_PLANS]: {
    title: "Building Plans",
    description: "Building applications, renovations, demolitions, and enforcement",
    icon: "mdi-office-building",
    chipColor: "primary",
  },
  [ACTION_CATEGORY.SCHEME_PLANS]: {
    title: "Scheme Plans",
    description: "Scheme plan applications for properties you represent",
    icon: "mdi-map-marker-multiple",
    chipColor: "teal",
  },
};

export function useProfessionalDashboard() {
  const userData = computed(() => store.getters[GET_USER_DATA]);

  const roleName = computed(() => userData.value?.profile?.role_name?.trim() ?? "");

  const displayRole = computed(() => getDisplayRoleName(roleName.value));

  const roleIcon = computed(() => {
    return ROLE_ICONS[roleName.value] || ROLE_ICONS[roleName.value.trim()] || "mdi-briefcase-account";
  });

  const quickActionGroups = computed(() => {
    const buildingActions = [];
    const schemeActions = [];

    if (hasPermission(EPORTAL_BUILDING_APPLICATION_CREATE)) {
      buildingActions.push({
        title: "New Building Application",
        subtitle: "Start a new building plan submission",
        category: ACTION_CATEGORY.BUILDING_PLANS,
        categoryLabel: CATEGORY_LABELS[ACTION_CATEGORY.BUILDING_PLANS],
        icon: "mdi-pencil-plus-outline",
        color: "secondary",
        route: routes.createBuildingApp,
      });
      buildingActions.push({
        title: "Building Drafts",
        subtitle: "Resume saved building applications",
        category: ACTION_CATEGORY.BUILDING_PLANS,
        categoryLabel: CATEGORY_LABELS[ACTION_CATEGORY.BUILDING_PLANS],
        icon: "mdi-file-document-edit-outline",
        color: "orange darken-2",
        route: routes.viewDrafts,
      });
    }

    if (hasPermission(EPORTAL_BUILDING_APPLICATION_VIEW)) {
      buildingActions.push({
        title: "Query Building Applications",
        subtitle: "Search and review building plans",
        category: ACTION_CATEGORY.BUILDING_PLANS,
        categoryLabel: CATEGORY_LABELS[ACTION_CATEGORY.BUILDING_PLANS],
        icon: "mdi-office-building-marker",
        color: "primary",
        route: routes.viewBuildings,
      });
      buildingActions.push({
        title: "Query Renovations",
        subtitle: "Building plan renovation cases",
        category: ACTION_CATEGORY.BUILDING_PLANS,
        categoryLabel: CATEGORY_LABELS[ACTION_CATEGORY.BUILDING_PLANS],
        icon: "mdi-hammer-wrench",
        color: "blue darken-2",
        route: routes.viewRenovations,
      });
      buildingActions.push({
        title: "Query Demolitions",
        subtitle: "Building plan demolition cases",
        category: ACTION_CATEGORY.BUILDING_PLANS,
        categoryLabel: CATEGORY_LABELS[ACTION_CATEGORY.BUILDING_PLANS],
        icon: "mdi-hammer",
        color: "brown darken-1",
        route: routes.viewDemolitions,
      });
    }

    if (hasPermission(EPORTAL_ENFORCEMENT_VIEW)) {
      buildingActions.push({
        title: "Query Enforcement",
        subtitle: "Building enforcement cases",
        category: ACTION_CATEGORY.BUILDING_PLANS,
        categoryLabel: CATEGORY_LABELS[ACTION_CATEGORY.BUILDING_PLANS],
        icon: "mdi-gavel",
        color: "red darken-2",
        route: routes.viewEnforcements,
      });
    }

    if (hasPermission(EPORTAL_SCHEME_PLAN_APPLICATION_CREATE)) {
      schemeActions.push({
        title: "New Scheme Plan Application",
        subtitle: "Create a scheme plan submission",
        category: ACTION_CATEGORY.SCHEME_PLANS,
        categoryLabel: CATEGORY_LABELS[ACTION_CATEGORY.SCHEME_PLANS],
        icon: "mdi-map-marker-plus",
        color: "teal darken-1",
        route: routes.createSchemePlanApplication,
      });
    }

    if (hasPermission(EPORTAL_SCHEME_PLAN_APPLICATION_VIEW)) {
      schemeActions.push({
        title: "Query Client Scheme Plans",
        subtitle: "Scheme plans for your clients",
        category: ACTION_CATEGORY.SCHEME_PLANS,
        categoryLabel: CATEGORY_LABELS[ACTION_CATEGORY.SCHEME_PLANS],
        icon: "mdi-map-search-outline",
        color: "indigo darken-1",
        route: routes.queryClientSchemePlanApplications,
      });
    }

    return [
      {
        ...CATEGORY_SECTIONS[ACTION_CATEGORY.BUILDING_PLANS],
        id: ACTION_CATEGORY.BUILDING_PLANS,
        actions: buildingActions,
      },
      {
        ...CATEGORY_SECTIONS[ACTION_CATEGORY.SCHEME_PLANS],
        id: ACTION_CATEGORY.SCHEME_PLANS,
        actions: schemeActions,
      },
    ].filter((group) => group.actions.length > 0);
  });

  const hasQuickActions = computed(() =>
    quickActionGroups.value.some((group) => group.actions.length > 0)
  );

  return {
    displayRole,
    roleIcon,
    quickActionGroups,
    hasQuickActions,
    roleName,
  };
}
